import { useCallback, useMemo, useState, useEffect } from "react";
import {
    ImageDefaultAudioDark,
    ImageDefaultAudioSmallDark,
    ImageDefaultCollectionDark,
    ImageDefaultCollectionSmallDark,
    ImageDefaultFileDark,
    ImageDefaultFileSmallDark,
    ImageDefaultImageDark,
    ImageDefaultImageSmallDark,
    ImageDefaultTextDark,
    ImageDefaultTextSmallDark,
    ImageDefaultVideoDark,
    ImageDefaultVideoSmallDark,
} from "@mzc-cloudplex/icons";
import {
    CardThumbnail,
    TableThumbnail,
    ThumbnailShadow,
    StyledCollectionThumbnail,
} from "./collection-thumbnail.styled";
import useThumbnailCrop from "@hooks/useThumbnailCrop";
import { Grid, Skeleton } from "@mzc-pdc/ui";
import {
    COLLECTION_THUMBNAIL_CARD_CONTAINER_SIZE,
    COLLECTION_THUMBNAIL_PANEL_CONTAINER_SIZE,
    COLLECTION_THUMBNAIL_TABLE_CONTAINER_SIZE,
} from "@constants";

const CollectionThumbnail = ({ collection, size = "default", variant = "card", selected, thumbnailCrop }) => {
    const { previewContainerStyle, previewImageStyle } = useThumbnailCrop({
        thumbnailCrop,
        variant,
        ignorePadding: true,
        cardContainerSize: COLLECTION_THUMBNAIL_CARD_CONTAINER_SIZE,
        tableContainerSize:
            variant === "table" ? COLLECTION_THUMBNAIL_TABLE_CONTAINER_SIZE : COLLECTION_THUMBNAIL_PANEL_CONTAINER_SIZE,
    });

    const [imageStatus, setImageStatus] = useState("loading");

    const preview = collection?.primaryAsset?.previews?.[0];

    useEffect(() => {
        if (!preview?.accessUrl) {
            setImageStatus("error");
            return;
        }

        setImageStatus("loading");

        const img = new Image();
        img.onload = () => {
            setImageStatus("success");
        };
        img.onerror = () => {
            setImageStatus("error");
        };
        img.src = preview.accessUrl;
    }, [preview?.accessUrl]);

    const getDefaultImageFromExtension = useCallback(
        (width, height, marginLeft) => {
            const mediaType = collection?.primaryAsset?.mediaType;

            if (variant === "table") {
                switch (mediaType) {
                    case "VIDEO":
                        return (
                            <ImageDefaultVideoSmallDark width={width} height={height} sx={{ marginLeft: marginLeft }} />
                        );
                    case "AUDIO":
                        return (
                            <ImageDefaultAudioSmallDark width={width} height={height} sx={{ marginLeft: marginLeft }} />
                        );
                    case "IMAGE":
                        return (
                            <ImageDefaultImageSmallDark width={width} height={height} sx={{ marginLeft: marginLeft }} />
                        );
                    case "TEXT":
                        return (
                            <ImageDefaultTextSmallDark width={width} height={height} sx={{ marginLeft: marginLeft }} />
                        );
                    case "FILE":
                        return (
                            <ImageDefaultFileSmallDark width={width} height={height} sx={{ marginLeft: marginLeft }} />
                        );
                    default:
                        return (
                            <ImageDefaultCollectionSmallDark
                                width={width}
                                height={height}
                                sx={{ marginLeft: marginLeft }}
                            />
                        );
                }
            } else {
                switch (mediaType) {
                    case "VIDEO":
                        return <ImageDefaultVideoDark width={width} height={height} sx={{ marginLeft: marginLeft }} />;
                    case "AUDIO":
                        return <ImageDefaultAudioDark width={width} height={height} sx={{ marginLeft: marginLeft }} />;
                    case "IMAGE":
                        return <ImageDefaultImageDark width={width} height={height} sx={{ marginLeft: marginLeft }} />;
                    case "TEXT":
                        return <ImageDefaultTextDark width={width} height={height} sx={{ marginLeft: marginLeft }} />;
                    case "FILE":
                        return <ImageDefaultFileDark width={width} height={height} sx={{ marginLeft: marginLeft }} />;
                    default:
                        return (
                            <ImageDefaultCollectionDark width={width} height={height} sx={{ marginLeft: marginLeft }} />
                        );
                }
            }
        },
        [collection, variant],
    );

    const getThumbnails = useMemo(() => {
        if (!collection) return <></>;

        const renderContent = () => {
            if (imageStatus === "loading") {
                return <></>;
            }

            if (imageStatus === "success" && preview?.accessUrl) {
                return (
                    <Grid item sx={previewContainerStyle}>
                        <img
                            src={preview.accessUrl}
                            alt={`collection-${variant}-preview-${collection.id}`}
                            draggable={false}
                        />
                    </Grid>
                );
            }

            return getDefaultImageFromExtension(variant === "table" ? 28 : 46, variant === "table" ? 28 : 60);
        };

        if (variant === "table") {
            return (
                <TableThumbnail item>
                    {renderContent()}
                    <ThumbnailShadow variant={"table"} />
                </TableThumbnail>
            );
        } else {
            return (
                <CardThumbnail item>
                    {renderContent()}
                    <ThumbnailShadow variant={variant === "card" ? "card" : "panel"} />
                </CardThumbnail>
            );
        }
    }, [collection, previewContainerStyle, imageStatus, variant, getDefaultImageFromExtension, preview]);

    return (
        <StyledCollectionThumbnail
            variant={variant}
            size={size}
            selected={selected}
            previewImageStyle={previewImageStyle}
        >
            {getThumbnails}
        </StyledCollectionThumbnail>
    );
};

export default CollectionThumbnail;
